// Number input for the "Repeat every {N}" recurrence interval. Holds the
// typed text in local draft state so the field can be transiently empty
// while the user replaces the digit — a fully controlled clamp would snap
// "" straight back to 1, making the existing value undeletable. Valid whole
// numbers >= 1 commit to the rule immediately (so the unit label and
// occurrence preview stay live); blur restores the last committed value
// when the draft is empty or invalid.

import { useState } from "react";

interface RecurrenceIntervalInputProps {
  id: string;
  /** Last committed interval from the recurrence rule. */
  value: number;
  /** Called with each valid whole-number interval (>= 1) as it is typed. */
  onCommit: (interval: number) => void;
  disabled?: boolean;
}

export default function RecurrenceIntervalInput({
  id,
  value,
  onCommit,
  disabled,
}: RecurrenceIntervalInputProps) {
  const [draft, setDraft] = useState(() => String(value));
  // Render-time resync: when the committed value changes from outside
  // (rule reloaded, occurrence preview round-trip), the draft follows it.
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setDraft(String(value));
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setDraft(next);
    const parsed = Number(next);
    if (next.trim() !== "" && Number.isInteger(parsed) && parsed >= 1) {
      onCommit(parsed);
    }
  };

  const handleBlur = () => {
    const parsed = Number(draft);
    if (draft.trim() === "" || !Number.isInteger(parsed) || parsed < 1) {
      setDraft(String(value));
    }
  };

  return (
    <input
      id={id}
      type="number"
      min={1}
      step={1}
      className="input w-20"
      value={draft}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
    />
  );
}
